import type { Locale } from '@/i18n/i18n';

/**
 * Objection-answer CTAs under four FAQ panels + the mobile sticky (conversion pack
 * 2026-10-28, spec 01). Copy is the approved text; the price line P and the stance S
 * are NOT repeated here: they are `pricing.betaFree` and `pricing.honestySubline`.
 *
 * Beta wording (open beta) and the EN translation of P are decided. UK copy is provisional
 * (owner/Oksi to check). A locale without an entry here renders no FAQ CTA and no sticky.
 */
export const FAQ_CTA_SLOTS = [
  'faq_beta_free',
  'faq_security',
  'faq_liability',
  'faq_switching',
] as const;
export type FaqCtaSlot = (typeof FAQ_CTA_SLOTS)[number];
export type HomeCtaSlot = FaqCtaSlot | 'sticky_mobile';

/** Which FAQ answer carries which slot (faq.q1 and faq.q2 both answer the beta question). */
export const FAQ_ITEM_SLOT: Record<string, FaqCtaSlot> = {
  q1: 'faq_beta_free',
  q2: 'faq_beta_free',
  q5: 'faq_liability',
  q7: 'faq_security',
  q8: 'faq_switching',
};

export interface FaqCtaCopy {
  headline: string;
  /** A verifiable one-line lead under the headline (security only). */
  lead?: string;
  /** Show the approved stance S as the body (liability). */
  stanceBody?: true;
  /** Which approved line sits under the button. */
  micro: 'price' | 'stance';
  link?: { label: string; href: '#pricing' | '/privacy' | '/terms' };
}

export interface HomeCtaCopy {
  faq: Record<FaqCtaSlot, FaqCtaCopy>;
  sticky: { pricing: string; dismiss: string };
}

export const HOME_CTA_COPY: Partial<Record<Locale, HomeCtaCopy>> = {
  pl: {
    faq: {
      faq_beta_free: {
        headline: 'Sprawdź Bookeper na własnych danych',
        micro: 'price',
        link: { label: 'Zobacz cennik', href: '#pricing' },
      },
      faq_security: {
        headline: 'Zobacz, jak chronimy Twoje dane, zanim cokolwiek dodasz',
        lead: 'Szyfrowana baza, izolacja wierszy (RLS) i logi dostępu. Szczegóły: Polityka prywatności.',
        micro: 'price',
        link: { label: 'Polityka prywatności', href: '/privacy' },
      },
      faq_liability: {
        headline: 'Każdą deklarację sprawdzasz i zatwierdzasz Ty',
        stanceBody: true,
        micro: 'price',
        link: { label: 'Regulamin', href: '/terms' },
      },
      faq_switching: {
        headline: 'Zmieniasz księgową w trakcie roku? Zacznij od swoich danych',
        micro: 'stance',
      },
    },
    sticky: { pricing: 'Cena', dismiss: 'Zamknij' },
  },
  en: {
    faq: {
      faq_beta_free: {
        headline: 'Try Bookeper with your own data',
        micro: 'price',
        link: { label: 'See pricing', href: '#pricing' },
      },
      faq_security: {
        headline: 'See how we protect your data before you add anything',
        lead: 'Encrypted database, row-level security and access logs. Details: Privacy Policy.',
        micro: 'price',
        link: { label: 'Privacy Policy', href: '/privacy' },
      },
      faq_liability: {
        headline: 'You review and approve every declaration',
        stanceBody: true,
        micro: 'price',
        link: { label: 'Terms', href: '/terms' },
      },
      faq_switching: {
        headline: 'Switching accountants mid-year? Start from your own data',
        micro: 'stance',
      },
    },
    sticky: { pricing: 'Pricing', dismiss: 'Close' },
  },
  uk: {
    faq: {
      faq_beta_free: {
        headline: 'Перевірте Bookeper на власних даних',
        micro: 'price',
        link: { label: 'Переглянути ціни', href: '#pricing' },
      },
      faq_security: {
        headline: 'Подивіться, як ми захищаємо Ваші дані, ще до того, як щось додасте',
        lead: 'Зашифрована база, ізоляція рядків (RLS) і журнали доступу. Деталі: Політика конфіденційності.',
        micro: 'price',
        link: { label: 'Політика конфіденційності', href: '/privacy' },
      },
      faq_liability: {
        headline: 'Кожну декларацію перевіряєте й затверджуєте Ви',
        stanceBody: true,
        micro: 'price',
        link: { label: 'Умови використання', href: '/terms' },
      },
      faq_switching: {
        headline: 'Змінюєте бухгалтера посеред року? Почніть зі своїх даних',
        micro: 'stance',
      },
    },
    sticky: { pricing: 'Ціни', dismiss: 'Закрити' },
  },
};
